import React, { useState } from 'react';
import { getProductImageURL } from '@noths/polaris-client-utils';
import Image from 'next/image';

import {
  NextButton,
  PreviousButton,
} from 'src/components/atoms/ImageNavigationButtons/ImageNavigationButtons';
import { ImageNumberLabel } from 'src/components/atoms/ImageNumberLabel/ImageNumberLabel';
import { ProductVideoSlide } from 'src/components/molecules/ProductVideoSlide/ProductVideoSlide';
import type {
  MediaItem,
  ProductImagesState,
} from 'src/components/organisms/ProductImagesCarousel/types';
import { useProductImagesCarousel } from 'src/components/organisms/ProductImagesCarousel/useProductImagesCarousel';
import { mediaKey } from '../ProductImagesCarousel/media/buildProductMedia';
import * as styles from './ProductOptionsDrawerImages.styles';

export const PRODUCT_OPTIONS_DRAWER_IMAGES_ID = 'product-options-drawer-images';
export const SECTION_LABEL = 'Product images';

export interface ProductOptionsDrawerImagesProps {
  media: MediaItem[];
}

export const ProductOptionsDrawerImages = ({ media }: ProductOptionsDrawerImagesProps) => {
  const totalImages = media.length;
  const [currentImage, setCurrentImage] = useState(0);
  const [navigatedBy, setNavigatedBy] = useState<ProductImagesState['navigatedBy']>(null);

  const [carouselContainerRef, carouselItemRefs] = useProductImagesCarousel(
    (entry) => {
      const imageIndex = Number((entry.target as HTMLElement).dataset.itemIndex);
      if (currentImage !== imageIndex) {
        setCurrentImage(imageIndex);
        setNavigatedBy('swipe');
      }
    },
    currentImage,
    navigatedBy,
    totalImages,
  );

  const goToImage = (imageIndex: number) => {
    setCurrentImage(imageIndex);
    setNavigatedBy('click');
  };

  return (
    <section aria-label={SECTION_LABEL} css={styles.stickyContainer}>
      <div css={styles.carouselWrapper}>
        <div css={styles.carousel} id={PRODUCT_OPTIONS_DRAWER_IMAGES_ID} ref={carouselContainerRef}>
          {media.map((item, i) => (
            <div
              css={styles.slide}
              data-item-index={i}
              key={mediaKey(item)}
              ref={(node) => {
                carouselItemRefs.current[i] = node as HTMLElement;
              }}
            >
              {item.type === 'video' ? (
                <ProductVideoSlide
                  contentPosition={i + 1}
                  isActive={currentImage === i}
                  // Not 'Main image': that fires an impression, which the main carousel
                  // already does for the same product view. 'Expanded image' suppresses it.
                  location="Expanded image"
                  playbackId={item.playbackId}
                  posterUrl={item.posterUrl}
                  title={`${item.title}, ${i + 1} of ${totalImages}`}
                />
              ) : (
                <Image
                  alt={`${item.title}, ${i + 1} of ${totalImages}`}
                  css={styles.image}
                  fill={true}
                  priority={i === 0}
                  src={getProductImageURL(item.href, 'normal')}
                  unoptimized
                />
              )}
            </div>
          ))}
        </div>
        {totalImages > 1 && (
          <>
            <PreviousButton
              controls={PRODUCT_OPTIONS_DRAWER_IMAGES_ID}
              onClick={() => goToImage(currentImage === 0 ? totalImages - 1 : currentImage - 1)}
            />
            <NextButton
              controls={PRODUCT_OPTIONS_DRAWER_IMAGES_ID}
              onClick={() => goToImage(currentImage === totalImages - 1 ? 0 : currentImage + 1)}
            />
            <ImageNumberLabel currentImage={currentImage + 1} totalImages={totalImages} />
          </>
        )}
      </div>
    </section>
  );
};
